import { useCallback, useEffect, useMemo, useState } from 'react';
import { reportsAPI } from '../../api';

// The catalog is static per deployment; fetch it once per session and share.
let cachedCatalog = null;
let inflight = null;

function loadCatalog(force = false) {
  if (cachedCatalog && !force) return Promise.resolve(cachedCatalog);
  if (inflight && !force) return inflight;
  inflight = reportsAPI
    .getDatasets()
    .then((res) => {
      cachedCatalog = res.data;
      return cachedCatalog;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useReportCatalog() {
  const [catalog, setCatalog] = useState(cachedCatalog);
  const [loading, setLoading] = useState(!cachedCatalog);
  const [error, setError] = useState(null);

  const load = useCallback((force = false) => {
    setLoading(true);
    setError(null);
    return loadCatalog(force)
      .then((c) => {
        setCatalog(c);
        return c;
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Could not load the report catalog');
        return null;
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!cachedCatalog) load(false);
  }, [load]);

  const datasets = useMemo(() => catalog?.datasets || [], [catalog]);
  const datasetByKey = useMemo(() => {
    const map = new Map();
    datasets.forEach((d) => map.set(d.key, d));
    return map;
  }, [datasets]);

  return {
    catalog,
    datasets,
    datasetByKey,
    presets: catalog?.presets || [],
    buckets: catalog?.buckets || [],
    opsByType: catalog?.ops_by_type || {},
    adhocAggsByType: catalog?.adhoc_aggs_by_type || {},
    sqlModeEnabled: !!catalog?.sql_mode_enabled,
    loading,
    error,
    reload: () => load(true),
  };
}
